import React, { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { getLocaleSlugs } from '../../util/helper'
import LeadQuestions from '../../components/Module/LeadQuestions'
import Layout from '../../components/Layout'
import { graphql } from 'gatsby'
import PageWrapper from '../../components/PageWrapper'
import i18n from '../../i18n/config'
import SEO from '../../components/SEO'
import { useInterval } from '../../util/hooks'
import { getAreaDisplay, getCategoryDisplay, getConditionDisplay } from '../../listings/helpers'
import { getProperty, getSimilarPropertiesBySlug } from '../../listings/api'
import listingInfoJson from '../../listings/components/linkedLayer'
import { trackFbq } from '../../util/helper'
import { useTranslation } from 'react-i18next'
import { Link } from 'gatsby'
import { Listing } from '@blokhub/blok-listing'
import '@blokhub/blok-listing/dist/style.css'

const COUNTRY = process.env.GATSBY_COUNTRY
const DEFAULT_LOCALE = process.env.GATSBY_DEFAULT_LOCALE

const Property = ({ data, slug }) => {
  const [listingInfo, setListingInfo] = useState(null)
  const [listingGuid, setListingGuid] = useState('')
  const [listingSold, setListingSold] = useState(false)
  const [similarApartments, setSimilarApartments] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dataLayerAdded, setDataLayerAdded] = useState(false)
  const [consentCookie, setConsentCookie] = useState(null)
  const [tracked, setTracked] = useState(false)
  const { t } = useTranslation()

  // Load listing data
  useEffect(() => {
    loadData(slug)
  }, [slug])

  // Set cookie
  useInterval(() => {
    setConsentCookie(Cookies.get('blok_consent_client'))
  }, !consentCookie ? 1000 : null)

  useEffect(() => {
    const timer = setTimeout(() => {
      addTracking(consentCookie, tracked, listingInfo)
    }, 5000)
    return () => clearTimeout(timer)
  }, [consentCookie, tracked, listingInfo])

  useEffect(() => {
    if (listingInfo && !dataLayerAdded) {
      window.dataLayer = window.dataLayer || []
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          apartmentCondition: getConditionDisplay(listingInfo.condition_general),
          apartmentRooms: listingInfo.room_count,
          price: listingInfo.price_display,
          buildingType: getCategoryDisplay(listingInfo.category),
          district: listingInfo.location_neighborhood,
          locality: listingInfo.location_municipality,
          postalCode: listingInfo.location_postcode,
        })
      }
      setDataLayerAdded(true)
    }
  }, [listingInfo, dataLayerAdded])

  function loadData(slug) {
    // fetch listingInfo
    setLoading(true)

    getProperty(slug)
    .then((res) => {
      setListingInfo(res)
      setListingGuid(res.case_id)
      setListingSold(!!res.sold_at)
      setLoading(false)
    })
    .catch(() => setLoading(false))

    loadSimilar(slug)
    .then((items) => {
      setSimilarApartments(items)
    })
    .catch(() => setSimilarApartments(null))
  }

  // fetch similar apartment from API
  async function loadSimilar(slug) {
    const { results } = await getSimilarPropertiesBySlug(slug)
    return results
  }

  function addTracking(consentCookie, tracked, listingInfo) {
    if (
      consentCookie &&
      !tracked &&
      listingInfo
    ) {
      const tracked = trackFbq('track', 'ViewContent', {
        apartmentCondition: getConditionDisplay(listingInfo.condition_general),
        apartmentRooms: listingInfo.room_count,
        apartmentPrice: listingInfo.price_display,
        buildingType: getCategoryDisplay(listingInfo.category),
        district: listingInfo.location_neighborhood,
        locality: listingInfo.location_municipality,
        postalCode: listingInfo.location_postcode,
        version: '25',
      })
      setTracked(tracked)
    }
  }

  function buildSEOData(listingInfo, country) {
    if (country === 'SE') {
      return {
        ogTitle: t('listing_meta__title_SE'),
        ogDescription: t('listing_meta__description_SE'),
        description: t('listing_meta__description_SE')
      }
    }
    if (country === 'FI' && listingInfo) {
      const {
        area_living, price_display, floorplan_description, hero,
        location_street_address, location_postcode, location_municipality
      } = listingInfo
      const title = `Blok – ${location_street_address}, ${location_postcode} ${location_municipality}`
      const ogTitle = `${getAreaDisplay(area_living)} | ${price_display} | ${location_street_address}, ${location_postcode} ${location_municipality}`
      return {
        title: title,
        ogTitle: ogTitle,
        description: floorplan_description,
        ogDescription: floorplan_description,
        ogImage: hero
      }
    }
    return {}
  }

  const metaData = buildSEOData(listingInfo, COUNTRY)
  const listingsUrls = getLocaleSlugs('listings')
  const localeBaseSlugs = getLocaleSlugs('property')
  const localeSlugs = {}

  for (const country in localeBaseSlugs) {
    localeSlugs[country] = localeBaseSlugs[country] + '/' + slug
  }

  const navConfig = {
    isTransparent: false,
    textColor: 'blue',
    localeSlugs,
  }
  const urlPrefix = DEFAULT_LOCALE === i18n.language ? '/' : `/${i18n.language}/`

  return (
    <>
      <SEO {...metaData}>
        <script type="application/ld+json">
          {listingInfoJson(listingInfo)}
        </script>
      </SEO>
      <Layout navConfig={navConfig}>
        <Listing
          listingInfo={listingInfo}
          listingGuid={listingGuid}
          listingSold={listingSold}
          preview={false}
          loading={loading}
          similarApartments={similarApartments}
          country={COUNTRY}
          singleBaseUrl={urlPrefix + localeBaseSlugs[i18n.language] + '/'}
          listingsUrl={urlPrefix + listingsUrls[i18n.language]}
          linkComponent={Link}
          lang={i18n.language}
        />
        {data?.contentfulLeadQuestions ? (
          <LeadQuestions data={data.contentfulLeadQuestions} />
        ) : null}
      </Layout>
    </>
  )
}

Property.Layout = PageWrapper
export default Property

export const pageQuery = graphql`
  query PropertyPageQuery(
    $country: String!
    $locale: String!
  ) {
    contentfulLeadQuestions(
      contentful_id: {
        in: ["6U2fLV2kRvpRNyANm5YQEK", "45rsjUXvQHfxqS9iO9BjVT"]
      }
      country: { eq: $country }
      node_locale: { eq: $locale }
    ) {
      ...ContentfulLeadQuestionsFragment
    }
  }
`
